import {
    Task,
    TaskStatus,
    TERMINATING_TASK_STATUSES,
} from "@common/types/task";
import {
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { View } from "@common/types/views/views";
import {
    PaginatedTransactionsDataContext,
    PaginatedTransactionsActionsContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import { TransactionsTaskView } from "./TransactionsTaskView";
import { TransactionsBulkActionsContextProvider } from "@components/transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import { TaskTransactionsViewFooter } from "@components/transactions/views/TaskTransactionsViewFooter";
import { useIsGlFirst } from "@hooks/useIsGlFirst";

import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { useCurrentUserIsWorkspaceOrganizationMember } from "@hooks/useCurrentUserIsWorkspaceOrganizationMember";
import { useTaskColumnResizing } from "@hooks/useTaskColumnResizing";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { isEqual } from "@common/helpers/comparison";
import { FiltersTypes } from "@common/types/filters/general";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { TRANSACTIONS_QUERY_KEY } from "@components/transactions/useTransactionsQuery";
import { useUpdateTask } from "@api/task.api";
import { StickyTaskMembershipPending } from "../shared/StickyTaskMembershipPending";
import {
    getMountedStickyTableFilters,
    stickyScopeEntityIds,
    useStickyRefreshOnMount,
} from "../shared/useStickyRefreshOnMount";
import taskBodyTableStyles from "../TaskBodyTable.module.scss";
import { LOAD_MORE_PAGINATION_MODE } from "@hooks/paginationMode";
import { useTransactionRefContextMaybe } from "../../TransactionRefContext";
import styles from "./TransactionsTask.module.scss";
import { LockedTaskFilters } from "./LockedTaskFilters";
import { getAvailableTaskFilterTabs } from "./taskTransactionFilters";
import { useLockedTaskFilters } from "./useLockedTaskFilters";

interface TransactionsTaskBodyProps {
    selectedTask: Task;
}

interface TransactionsTaskBodyContentProps extends TransactionsTaskBodyProps {
    resolvedMembership?: FiltersTypes;
}

const TransactionsTaskBodyContent = ({
    selectedTask,
    resolvedMembership,
}: TransactionsTaskBodyContentProps) => {
    const { transactionCount, filters } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setCurrentFilters } = useContext(
        PaginatedTransactionsActionsContext,
    );
    const { isOrganizationMember } =
        useCurrentUserIsWorkspaceOrganizationMember();
    const updateTask = useUpdateTask(selectedTask);
    const trxRefCtx = useTransactionRefContextMaybe();

    const currentFiltersRef = useRef(filters);
    currentFiltersRef.current = filters;
    const stickyTransactionIds =
        resolvedMembership?.transactionIds ??
        selectedTask.view?.filters?.transactionIds;
    useEffect(() => {
        if (
            !selectedTask.view?.originalFilters ||
            !stickyTransactionIds?.length
        ) {
            return;
        }
        const currentFilters = currentFiltersRef.current;
        if (isEqual(currentFilters.transactionIds, stickyTransactionIds)) {
            return;
        }
        setCurrentFilters({
            ...currentFilters,
            transactionIds: stickyTransactionIds,
            entityIds: currentFilters.entityIds?.length
                ? currentFilters.entityIds
                : stickyScopeEntityIds(selectedTask),
        });
    }, [
        selectedTask,
        selectedTask.view?.originalFilters,
        stickyTransactionIds,
        setCurrentFilters,
    ]);

    const baseViewConfig = useTransactionViewConfig();

    const baseView = useMemo(() => {
        const parsed = TransactionViewSchema.nullable().safeParse(
            selectedTask.view
                ? {
                      ...selectedTask.view,
                      options: {
                          ...selectedTask.view.options,
                          selection: true,
                          bulkActions: true,
                      },
                  }
                : null,
        );
        return parsed.success ? (parsed.data ?? undefined) : undefined;
    }, [selectedTask.view]);

    const { applyColumnSizesToView, onViewChange: handleColumnSizingChange } =
        useTaskColumnResizing(selectedTask.id);

    const viewToPass = useMemo(
        () => (baseView ? applyColumnSizesToView(baseView) : undefined),
        [baseView, applyColumnSizesToView],
    );

    // Restrict ColumnSettings to only the columns the task creator included.
    // viewToPass is undefined only when baseView is undefined (guarded below).
    const taskColumnIds = useMemo(
        () => new Set((viewToPass?.columns ?? []).map((col) => col.id)),
        [viewToPass],
    );

    const viewConfigForColumnSettings = useMemo(
        () => ({
            ...baseViewConfig,
            columns: Object.fromEntries(
                Object.entries(baseViewConfig.columns).filter(([id]) =>
                    taskColumnIds.has(id),
                ),
            ),
        }),
        [baseViewConfig, taskColumnIds],
    );

    // Local view state for the user's column show/hide/order edits and view
    // option edits (e.g. density). NOT persisted to localStorage — fresh slate
    // per task open. Column sizes continue to be cached via useTaskColumnResizing.
    // Only the user's edits are stored locally; the rest of the view is always
    // derived live from viewToPass so sticky-refresh view changes are not frozen
    // in a mount-time snapshot.
    const [columnOverrides, setColumnOverrides] = useState<
        View["columns"] | null
    >(null);
    const [optionsOverrides, setOptionsOverrides] =
        useState<View["options"]>(null);

    const currentView = useMemo(() => {
        if (!viewToPass) {
            return undefined;
        }
        if (!columnOverrides && !optionsOverrides) {
            return viewToPass;
        }
        // Re-validate the merged view so currentView stays a fully-typed
        // TransactionView (columns/options arrive as the base View shape from the
        // onViewChange callbacks). Fall back to the live view if parsing fails.
        const merged = TransactionViewSchema.safeParse({
            ...viewToPass,
            columns: columnOverrides ?? viewToPass.columns,
            options: {
                ...viewToPass.options,
                ...optionsOverrides,
                selection: viewToPass.options?.selection,
                bulkActions: viewToPass.options?.bulkActions,
            },
        });
        return merged.success ? merged.data : viewToPass;
    }, [viewToPass, columnOverrides, optionsOverrides]);

    // Unified view change handler: persist column sizes to cache +
    // update local column visibility / ordering + view option (density) state.
    const handleViewChange = useCallback(
        (updatedView: View) => {
            handleColumnSizingChange(updatedView);
            setColumnOverrides(updatedView.columns);
            setOptionsOverrides(updatedView.options ?? null);
        },
        [handleColumnSizingChange],
    );
    // For sticky tasks the actual creator-set filter conditions are stored in
    // `originalFilters`; `view.filters` only holds the resolved transactionIds.
    // Use originalFilters when present so those conditions render as locked pills.
    const baseFilters = useMemo(
        () =>
            selectedTask.view?.originalFilters ??
            selectedTask.view?.filters ??
            {},
        [selectedTask.view?.originalFilters, selectedTask.view?.filters],
    );

    const { lockedActiveFilters, displayFilters, handleFiltersChange } =
        useLockedTaskFilters(baseFilters);

    const { enabled: isGlFirst } = useIsGlFirst();

    const availableFilters = useMemo(
        () =>
            getAvailableTaskFilterTabs(
                viewToPass?.columns ?? [],
                lockedActiveFilters,
                { isGlFirst },
            ),
        [viewToPass?.columns, lockedActiveFilters, isGlFirst],
    );

    const isSpecificTransactionTask =
        !!viewToPass?.filters?.transactionIds?.length;

    const handleSearchChange = useCallback(
        (value: string) => {
            setCurrentFilters({ ...filters, search: value });
        },
        [filters, setCurrentFilters],
    );

    const emptyState = useMemo(() => {
        if (isSpecificTransactionTask) {
            return (
                <EmptyState header="No transactions associated with this task" />
            );
        }
        const canMarkDone = !TERMINATING_TASK_STATUSES.includes(
            selectedTask.status,
        );
        return (
            <EmptyState
                header="No more transactions matching the task definition"
                ctaText="Mark done"
                action={
                    canMarkDone
                        ? () => updateTask.mutate({ status: TaskStatus.DONE })
                        : undefined
                }
            />
        );
    }, [isSpecificTransactionTask, selectedTask.status, updateTask]);

    if (!viewToPass || !currentView) {
        return null;
    }

    return (
        <TransactionsBulkActionsContextProvider
            currentFilters={filters}
            totalTransactions={transactionCount}
            onUpdated={() => {}}
        >
            <TablePageHeader
                isFullPage
                borderBottom
                search={filters.search ?? ""}
                onSearchChange={handleSearchChange}
                filterContainerClassName={styles.filterContainer}
                filters={displayFilters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                afterFiltersChildren={
                    <LockedTaskFilters
                        lockedActiveFilters={lockedActiveFilters}
                        baseFilters={baseFilters}
                    />
                }
            >
                <ColumnSettings<TransactionView>
                    view={currentView}
                    viewConfig={viewConfigForColumnSettings}
                    defaultView={viewToPass}
                    onViewChange={handleViewChange}
                />
            </TablePageHeader>
            <div
                className={taskBodyTableStyles.viewTableWithoutTopBorderWrapper}
            >
                <TransactionsTaskView
                    view={currentView}
                    selectedTransactionId={
                        trxRefCtx?.focusedTransactionId ?? undefined
                    }
                    fixedTopOffsetPx
                    // The app header hides via a `body` class when
                    // fullscreen, which only shifts this table's position,
                    // not its own size — the auto top-offset measurement's
                    // ResizeObserver alone wouldn't catch it, so force a
                    // re-measure on the flag itself.
                    emptyView={() => emptyState}
                    className={styles.transactionsTable}
                    onViewChange={handleViewChange}
                    footer={(options) => (
                        <TaskTransactionsViewFooter
                            {...options}
                            view={currentView}
                            isOrganizationMember={isOrganizationMember}
                        />
                    )}
                    footerClassName={styles.footerActionBar}
                />
            </div>
        </TransactionsBulkActionsContextProvider>
    );
};

export const TransactionsTaskBody = ({
    selectedTask,
}: TransactionsTaskBodyProps) => {
    const queryClient = useQueryClient();
    const invalidateTransactionsList = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: [TRANSACTIONS_QUERY_KEY],
        });
    }, [queryClient]);
    const { isMembershipPending, resolvedMembership } = useStickyRefreshOnMount(
        {
            selectedTask,
            onMembershipAdded: invalidateTransactionsList,
        },
    );
    // Memoized so `parentFilters` keeps a stable reference across re-renders
    // (e.g. page changes) — an unstable reference would reset user-added
    // filters back to the creator-set ones.
    const parsedDefaultView = useMemo(
        () => TransactionViewSchema.safeParse(selectedTask.view),
        [selectedTask.view],
    );
    const tableFilters = useMemo(
        () =>
            getMountedStickyTableFilters({
                isMembershipPending,
                viewFilters: parsedDefaultView.success
                    ? parsedDefaultView.data.filters
                    : undefined,
                resolvedMembership,
                scopeEntityIds: stickyScopeEntityIds(selectedTask),
            }),
        [
            isMembershipPending,
            parsedDefaultView,
            resolvedMembership,
            selectedTask,
        ],
    );
    const viewForTable = useMemo(() => {
        if (!parsedDefaultView.success || tableFilters === undefined) {
            return undefined;
        }
        return {
            ...parsedDefaultView.data,
            filters: tableFilters,
        };
    }, [parsedDefaultView, tableFilters]);

    if (!selectedTask.view || !parsedDefaultView.success) {
        return null;
    }

    if (!viewForTable) {
        return <StickyTaskMembershipPending />;
    }

    return (
        <PaginatedTransactionsTableContextProvider
            defaultView={viewForTable}
            paginationMode={LOAD_MORE_PAGINATION_MODE}
            parentFilters={viewForTable.filters}
            key={selectedTask.id}
        >
            <TransactionsTaskBodyContent
                selectedTask={selectedTask}
                resolvedMembership={resolvedMembership}
            />
        </PaginatedTransactionsTableContextProvider>
    );
};
